import icArrowLeft from "@/assets/icons/ic-arrow-left.svg";
import IcCalendar from "@/assets/icons/ic-calendar-inquiry-01.svg";
import IconName from "@/assets/icons/ic-inquiry-user.svg";
import IcMail01 from "@/assets/icons/ic-my-email.svg";
import IcPhone from "@/assets/icons/ic-my-phone.svg";
import IcSendDisable from "@/assets/icons/ic-send-disable.svg";
import IcSend from "@/assets/icons/ic-send.svg";
import IcUserCircle from "@/assets/icons/ic-user-circle.svg";
import IcUserInquiry from "@/assets/icons/ic-user-inquiry.svg";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TruncatedTooltip } from "@/components/ui/truncated-tooltip";
import { formatDate, formatPhoneNumber } from "@/lib/utils";
import { getInquiryDetail, sendInquiryReply, type InquiryDetail } from "@/services/inquiry-service";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { InfoItem, InfoRow } from "./components/inquiry-info-display";
import { InquiryStatusBadge } from "./components/inquiry-status-badge";
import { MessageBubble } from "./components/message-bubble";

export default function InquiryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [inquiry, setInquiry] = useState<InquiryDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [activeTab, setActiveTab] = useState<"info" | "chat">("info");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const adminMessages = inquiry?.messages?.filter((m) => m.sender_role === "admin");
  const lastAdminName = adminMessages?.[adminMessages.length - 1]?.sender_name;

  const scrollToBottom = (behavior: ScrollBehavior = "instant") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [replyText]);

  useEffect(() => {
    if (!id) return;
    const fetchDetail = async () => {
      try {
        const data = await getInquiryDetail(id);
        setInquiry(data);
      } catch {
        setInquiry(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetail();
  }, [id]);

  useEffect(() => {
    if (inquiry?.messages?.length && (activeTab === "chat" || window.innerWidth >= 1024)) {
      // Double rAF: when the chat panel switches from `hidden` to visible,
      // its scroll height is only computed after the next layout pass.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToBottom();
        });
      });
    }
  }, [inquiry?.messages?.length, activeTab]);

  const handleSendReply = async () => {
    if (!id || !replyText.trim() || isSending) return;
    setIsSending(true);
    try {
      const newReply = await sendInquiryReply(id, replyText.trim());
      const replyWithName = {
        ...newReply,
        sender_name: newReply.sender_name || inquiry?.user_info?.requester_name || "",
      };
      setInquiry((prev) =>
        prev ? { ...prev, messages: [...(prev.messages ?? []), replyWithName] } : prev,
      );
      setReplyText("");
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  if (!inquiry) {
    return <></>;
  }

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div style={{ display: isLoading ? "none" : "block" }}>
        {/* Header */}
        <div className="mx-auto max-w-[1120px] px-5 min-[1120px]:px-0 px-5 pt-6 lg:pt-10">
          <div className="flex flex-col items-start gap-4 lg:flex-row lg:items-center ">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="h-12 w-12 bg-transparent"
            >
              <img src={icArrowLeft} alt="Back" />
            </Button>
            <h2 className="text-xl font-bold leading-7 tracking-[-0.02em] text-[#202020]">
              <span className="lg:hidden">{inquiry.title}</span>
              <span className="hidden lg:inline">뒤로 가기</span>
            </h2>
          </div>
          <div className="hidden h-px w-full bg-black/[0.09] lg:block mt-6" />
        </div>

        <div className="mx-auto flex max-w-[850px] flex-1 flex-col items-center gap-0  lg:pb-20">
          {/* Mobile Tab Bar */}
          <Tabs
            value={activeTab}
            onValueChange={(val) => setActiveTab(val as "info" | "chat")}
            className="mt-6 flex w-full  lg:hidden"
          >
            <TabsList className="h-[48px] !px-5 w-full items-stretch justify-start gap-6 rounded-none border-b border-black/[0.15] bg-transparent p-0">
              <TabsTrigger
                value="info"
                className="relative -mb-[1px] rounded-none border-b-2 border-transparent px-0 py-3 text-base font-medium tracking-[-0.01em] text-[#8D8D8D] data-[state=active]:border-[#FF3B00] data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:text-[#202020] data-[state=active]:shadow-none"
              >
                문의 정보
              </TabsTrigger>
              <TabsTrigger
                value="chat"
                className="relative -mb-[1px] rounded-none border-b-2 border-transparent px-0 py-3 text-base font-medium tracking-[-0.01em] text-[#8D8D8D] data-[state=active]:border-[#FF3B00] data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:text-[#202020] data-[state=active]:shadow-none"
              >
                문의 내용
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Desktop Divider */}

          {/* Content */}
          <div className="flex w-full flex-col gap-6 px-5 pt-6 lg:px-0 pb-5 lg:pb-0">
            {/* Tab: 문의 정보 */}
            <div className={`flex-col gap-6 ${activeTab === "info" ? "flex" : "hidden"} lg:flex`}>
              {/* Title + Meta */}
              <div className="flex flex-col gap-6 rounded p-4 lg:p-6 border border-black/[0.09]">
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                    <h1 className="flex-1 font-[Georgia] text-[30px] font-normal leading-9 tracking-[-0.0213em] text-[#202020] lg:text-[32px] lg:leading-10 lg:tracking-[-0.03em]">
                      {inquiry.title}
                    </h1>
                    <InquiryStatusBadge status={inquiry.status} />
                  </div>
                  <hr className="hidden lg:block border-black/[0.15]" />
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex w-20 items-center gap-2">
                        <img src={IcCalendar} alt="" className="h-5 w-5" />
                        <span className="text-sm text-[#000000] shrink-0">접수일시</span>
                      </div>
                      <span className="text-base text-[#000000] translate-y-[2px]">
                        {formatDate(inquiry.created_at, "YYYY-MM-DD HH:mm")}
                      </span>
                    </div>
                    {inquiry.last_reply_at && (
                      <div className="flex items-baseline gap-3">
                        <div className="flex w-20 items-baseline gap-2">
                          <img src={IcCalendar} alt="" className="h-5 w-5 self-center" />
                          <span className="text-sm text-[#000000] shrink-0">답변일시</span>
                        </div>
                        <span className="text-base text-[#000000] translate-y-[2px]">
                          {formatDate(inquiry.last_reply_at, "YYYY-MM-DD HH:mm")}
                        </span>
                      </div>
                    )}
                    {lastAdminName && (
                      <div className="flex items-baseline gap-3">
                        <div className="flex items-baseline gap-2 w-[80px] md:w-fit">
                          <img src={IcUserInquiry} alt="" className="h-5 w-5 self-center" />
                          <span className="text-sm text-[#000000] shrink-0 ">담당자</span>
                        </div>
                        <TruncatedTooltip className="text-base text-[#000000] translate-y-[2px] max-w-[200px] md:max-w-[120px]  truncate">
                          {lastAdminName}
                        </TruncatedTooltip>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 문의자 정보 */}
              <div className="flex flex-col gap-3 order-2 lg:order-1">
                <span className="text-xs text-[#000000]">문의자 정보</span>
                <div className="hidden justify-between gap-4 rounded p-5 lg:flex border border-black/[0.09]">
                  <InfoItem
                    icon={<img src={IconName} alt="" className="h-7 w-7" />}
                    label="이름"
                    value={inquiry?.user_info?.requester_name}
                    cssLabel="w-[100px]"
                  />
                  <InfoItem
                    icon={<img src={IcPhone} alt="" className="h-7 w-7" />}
                    label="전화번호"
                    value={formatPhoneNumber(inquiry.user_info?.phone || "") || "-"}
                  />
                  <InfoItem
                    icon={<img src={IcMail01} alt="" className="h-7 w-7" />}
                    label="이메일"
                    value={inquiry.user_info?.email || "-"}
                    cssLabel="w-[140px]"
                  />
                  <InfoItem
                    icon={<img src={IcUserCircle} alt="" className="h-7 w-7" />}
                    label="직급"
                    value={inquiry.user_info?.position || "-"}
                    cssLabel="w-[100px]"
                  />
                </div>
                <div className="flex flex-col rounded-[4px] border border-black/[0.15] bg-white px-4 py-2 lg:hidden">
                  <InfoRow label="이름" value={inquiry.user_info?.requester_name || "-"} />
                  <InfoRow
                    label="전화번호"
                    value={formatPhoneNumber(inquiry.user_info?.phone || "") || "-"}
                  />
                  <InfoRow label="이메일" value={inquiry.user_info?.email || "-"} />
                  <InfoRow label="직급" value={inquiry.user_info?.position || "-"} isLast />
                </div>
              </div>

              {/* 선택한 컨설팅 */}
              {inquiry.selected_consulting && (
                <div className="flex flex-col gap-3 order-1 lg:order-2">
                  <span className="text-xs text-[#000000]">선택한 컨설팅</span>
                  <div className="hrounded px-4 py-3 lg:flex border border-[rgba(0,0,0,0.09)] text-[#BBBBBB] text-[14px] font-bold leading-6">
                    {inquiry.selected_consulting}
                  </div>
                </div>
              )}

              {/* 첨부 에이전트 */}
              {(inquiry.agents?.length ?? 0) > 0 && (
                <div className="flex flex-col gap-3 order-3 lg:pb-0">
                  <span className="text-xs text-[#000000]">첨부 에이전트</span>
                  <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:justify-between">
                    {inquiry.agents?.map((agent) => (
                      <div
                        key={agent.id}
                        className="flex w-full md:w-[410px] items-center gap-3 rounded border border-black/[0.09] bg-white p-4"
                      >
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <span className="truncate text-sm font-medium text-[#000000]">
                            {agent.name}
                          </span>
                          <span className="truncate text-xs text-[#000000]">
                            {agent.provider || "-"}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => navigate(`/agents/${agent.id}`)}
                          className="text-xs font-bold text-[#000000]"
                        >
                          상세 보기
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Tab: 문의 내용 */}
            <div className={`flex-col gap-3 ${activeTab === "chat" ? "flex" : "hidden"} lg:flex`}>
              <span className="hidden text-xs text-[#000000] lg:block">문의 내용</span>
              <div className="relative flex flex-col rounded lg:border lg:border-black/[0.09] pt-5  pb-10 lg:p-[19px] h-[550px] min-h-[200px] lg:max-h-[810px]">
                <div className="flex-1 flex flex-col gap-6 overflow-y-auto lg:pr-2 custom-scrollbar ">
                  <div className="mt-auto" />
                  {(inquiry.messages ?? []).map((reply) => (
                    <MessageBubble key={reply.id} reply={reply} />
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                <div className="flex flex-col items-center pt-4 lg:pt-10 shrink-0">
                  <div className="flex w-full flex-col rounded border border-black/[0.09] bg-white px-2 pb-2 pt-5 shadow-[0px_4px_20px_-8px_rgba(0,0,0,0.09),0px_0px_32px_0px_rgba(0,0,0,0.07)]">
                    <div className="flex items-center gap-[10px] px-3">
                      <textarea
                        ref={textareaRef}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="작성중인 요청사항..."
                        className="w-full max-h-[300px] min-h-[70px]  resize-none bg-transparent text-base font-normal leading-6 tracking-[-0.01em] text-[#000000] placeholder:text-[#B0B0B0] outline-none"
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleSendReply}
                        disabled={!replyText.trim() || isSending}
                        className="flex h-8 w-8  items-center justify-center rounded-[4px] bg-[#FFAA72] text-black disabled:bg-[#E0E0E0]  "
                      >
                        <img
                          src={!replyText.trim() || isSending ? IcSendDisable : IcSend}
                          alt="send"
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
